import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '亚洲春色',
    url: 'https://hvrxld.cn',
    description: aboutInfo.introduction,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
    },
  };

  const jsonLdFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 亚洲春色"
        description={aboutInfo.introduction}
        keywords="亚洲春色,关于我们,品牌介绍,春季旅行,联系方式"
        canonicalUrl="https://hvrxld.cn/about"
        jsonLd={jsonLdOrg}
      />
      <Navbar />

      <main className="pt-16">
        <div className="relative w-full h-[350px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Asian%20spring%20landscape%20panorama%20with%20cherry%20blossoms%20and%20tea%20plantations%2C%20diverse%20Asian%20scenery%2C%20warm%20morning%20light%2C%20wide%20cinematic%20view%2C%20peaceful%20and%20inspiring%20atmosphere%2C%20travel%20editorial%20photography&width=1920&height=600&seq=about-hero-01&orientation=landscape"
            alt="关于亚洲春色"
            title="关于亚洲春色 - 感受春日亚洲最美风情"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white">关于我们</h1>
          </div>
        </div>

        <div className="px-6 md:px-10 py-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

            <section className="mt-8">
              <h2 className="text-2xl font-bold text-foreground-950 mb-4">品牌故事</h2>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">{aboutInfo.introduction}</p>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">{aboutInfo.story}</p>
            </section>

            <section className="mt-16">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">我们的数据</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                    <p className="text-3xl font-bold text-primary-500 mb-1">{stat.value}</p>
                    <p className="text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">品牌价值观</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aboutInfo.values.map((v) => (
                  <div key={v.title} className="p-6 rounded-lg bg-background-100 border border-background-200/60">
                    <h3 className="text-lg font-semibold text-foreground-950 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">读者口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aboutInfo.reviews.map((review, idx) => (
                  <div
                    key={idx}
                    className="p-6 rounded-lg bg-background-100 border border-background-200/60 flex flex-col"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed flex-1 mb-4">
                      &ldquo;{review.content}&rdquo;
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="font-medium text-foreground-800">{review.name}</span>
                      <span>{review.location} · {review.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16 mb-8">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">邮箱</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电话</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">地址</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">微信公众号</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-weibo-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">微博</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.weibo}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-16 mb-12">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
              <div className="space-y-4">
                {aboutInfo.faqs.map((faq, idx) => (
                  <details key={idx} className="group rounded-lg bg-background-100 border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer text-foreground-950 font-medium text-sm md:text-base">
                      {faq.question}
                      <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180 flex-shrink-0">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFAQ) }}
              />
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}